import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Users, 
  Plus, 
  Phone, 
  Calendar,
  ChevronRight,
  Filter,
  TrendingUp,
  UserPlus,
  Target,
  RotateCcw
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

const LEAD_SOURCES = [
  { value: "walk_in", label: "Walk-in" },
  { value: "phone", label: "Phone Call" },
  { value: "website", label: "Website" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "referral", label: "Referral" },
  { value: "doctor_referral", label: "Doctor Referral" },
  { value: "camp", label: "Health Camp" },
  { value: "advertisement", label: "Advertisement" },
  { value: "social_media", label: "Social Media" },
  { value: "other", label: "Other" }
];

const LEAD_STAGES = [
  { value: "new", label: "New", color: "bg-blue-100 text-blue-700", icon: "🆕" },
  { value: "contacted", label: "Contacted", color: "bg-purple-100 text-purple-700", icon: "📞" },
  { value: "appointment_scheduled", label: "Appointment", color: "bg-amber-100 text-amber-700", icon: "📅" },
  { value: "test_done", label: "Test Done", color: "bg-cyan-100 text-cyan-700", icon: "🔬" },
  { value: "recommendation_given", label: "Recommended", color: "bg-indigo-100 text-indigo-700", icon: "📋" },
  { value: "trial_started", label: "Trial", color: "bg-orange-100 text-orange-700", icon: "👂" },
  { value: "negotiation", label: "Negotiation", color: "bg-yellow-100 text-yellow-700", icon: "💬" },
  { value: "purchased", label: "Purchased", color: "bg-emerald-100 text-emerald-700", icon: "✅" },
  { value: "lost", label: "Lost", color: "bg-red-100 text-red-700", icon: "❌" },
  { value: "reopened", label: "Reopened", color: "bg-teal-100 text-teal-700", icon: "🔄" }
];

export default function LeadsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [leads, setLeads] = useState([]);
  const [patients, setPatients] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [stageDialogOpen, setStageDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  
  const [filters, setFilters] = useState({
    stage: "",
    lead_source: "",
    clinic_id: ""
  });

  const [newLead, setNewLead] = useState({
    patient_id: "",
    clinic_id: "",
    lead_source: "walk_in",
    lead_source_detail: "",
    assigned_to: "",
    notes: ""
  });

  const [stageUpdate, setStageUpdate] = useState({
    stage: "",
    notes: "",
    next_follow_up_date: ""
  });

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.stage) params.append("stage", filters.stage);
      if (filters.lead_source) params.append("lead_source", filters.lead_source);
      if (filters.clinic_id) params.append("clinic_id", filters.clinic_id);

      const [leadsRes, patientsRes, clinicsRes, usersRes] = await Promise.all([
        apiClient.get(`/leads?${params.toString()}`),
        apiClient.get("/patients"),
        apiClient.get("/clinics"),
        apiClient.get("/users").catch(() => ({ data: [] }))
      ]);
      setLeads(leadsRes.data);
      setPatients(patientsRes.data);
      setClinics(clinicsRes.data);
      setUsers(usersRes.data);
      
      if (clinicsRes.data.length > 0) {
        setNewLead(prev => ({ ...prev, clinic_id: clinicsRes.data[0].id }));
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLead = async (e) => {
    e.preventDefault();
    try {
      const response = await apiClient.post("/leads", newLead);
      setLeads([response.data, ...leads]);
      toast.success("Lead created successfully!");
      setDialogOpen(false);
      setNewLead({
        patient_id: "",
        clinic_id: clinics[0]?.id || "",
        lead_source: "walk_in",
        lead_source_detail: "",
        assigned_to: "",
        notes: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create lead");
    }
  };

  const handleUpdateStage = async () => {
    if (!selectedLead) return;
    try {
      await apiClient.put(`/leads/${selectedLead.id}/stage`, stageUpdate);
      toast.success("Lead stage updated!");
      setStageDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to update stage");
    }
  };

  const handleReopenLead = async (leadId) => {
    try {
      await apiClient.post(`/leads/${leadId}/reopen`, null, {
        params: { notes: "Reopened for post-sale follow-up" }
      });
      toast.success("Lead reopened!");
      fetchData();
    } catch (error) {
      toast.error("Failed to reopen lead");
    }
  };

  const getPatientInfo = (patientId) => {
    const patient = patients.find(p => p.id === patientId);
    return patient || { name: "Unknown", phone: "" };
  };

  const getStageInfo = (stage) => {
    return LEAD_STAGES.find(s => s.value === stage) || LEAD_STAGES[0];
  };

  const getSourceLabel = (source) => {
    return LEAD_SOURCES.find(s => s.value === source)?.label || source;
  };

  // Pipeline stats
  const stats = LEAD_STAGES.reduce((acc, stage) => {
    acc[stage.value] = leads.filter(l => l.stage === stage.value).length;
    return acc;
  }, {});

  const conversionRate = leads.length > 0 
    ? ((stats.purchased / leads.length) * 100).toFixed(1) 
    : 0;

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="leads-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope'] flex items-center gap-2">
              <Target className="w-7 h-7 text-indigo-600" />
              Sales Pipeline
            </h1>
            <p className="text-slate-500">Track leads from inquiry to purchase</p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-lead-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                New Lead
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Lead</DialogTitle>
                <DialogDescription>Track a new patient lead in the sales pipeline</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateLead} className="space-y-4 mt-4">
                <div>
                  <Label>Patient *</Label>
                  <Select value={newLead.patient_id} onValueChange={(v) => setNewLead({...newLead, patient_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Select patient" /></SelectTrigger>
                    <SelectContent>
                      {patients.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name} ({p.phone})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Lead Source *</Label>
                  <Select value={newLead.lead_source} onValueChange={(v) => setNewLead({...newLead, lead_source: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LEAD_SOURCES.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Source Details</Label>
                  <Input
                    placeholder="e.g., Google Ad, Dr. Sharma referral..."
                    value={newLead.lead_source_detail}
                    onChange={(e) => setNewLead({...newLead, lead_source_detail: e.target.value})}
                  />
                </div>

                <div>
                  <Label>Assign To</Label>
                  <Select value={newLead.assigned_to} onValueChange={(v) => setNewLead({...newLead, assigned_to: v})}>
                    <SelectTrigger><SelectValue placeholder="Select team member" /></SelectTrigger>
                    <SelectContent>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={!newLead.patient_id}>
                    Create Lead
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{stats.new || 0}</p>
              <p className="text-xs text-slate-500">New Leads</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">{stats.test_done || 0}</p>
              <p className="text-xs text-slate-500">Tests Done</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-orange-600">{stats.trial_started || 0}</p>
              <p className="text-xs text-slate-500">In Trial</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-emerald-600">{stats.purchased || 0}</p>
              <p className="text-xs text-slate-500">Purchased</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-indigo-600">{conversionRate}%</p>
              <p className="text-xs text-slate-500">Conversion</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              <Filter className="w-4 h-4 text-slate-500" />
              <Select value={filters.stage || "all"} onValueChange={(v) => setFilters({...filters, stage: v === "all" ? "" : v})}>
                <SelectTrigger className="w-40"><SelectValue placeholder="All Stages" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stages</SelectItem>
                  {LEAD_STAGES.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.icon} {s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filters.lead_source || "all"} onValueChange={(v) => setFilters({...filters, lead_source: v === "all" ? "" : v})}>
                <SelectTrigger className="w-40"><SelectValue placeholder="All Sources" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {LEAD_SOURCES.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(filters.stage || filters.lead_source) && (
                <Button variant="ghost" size="sm" onClick={() => setFilters({ stage: "", lead_source: "", clinic_id: "" })}>
                  Clear Filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Leads List */}
        <Card>
          <CardHeader>
            <CardTitle>All Leads ({leads.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">
                {[1, 2, 3].map(i => <div key={i} className="skeleton h-20 rounded-lg"></div>)}
              </div>
            ) : leads.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {leads.map(lead => {
                  const patient = getPatientInfo(lead.patient_id);
                  const stageInfo = getStageInfo(lead.stage);
                  
                  return (
                    <div 
                      key={lead.id} 
                      className="p-4 hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/patients/${lead.patient_id}`)}
                      data-testid={`lead-${lead.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center text-lg">
                            {stageInfo.icon}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-slate-900">{patient.name}</p>
                              <Badge className={stageInfo.color}>{stageInfo.label}</Badge>
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {patient.phone}
                              </span>
                              <span>Source: {getSourceLabel(lead.lead_source)}</span>
                              {lead.follow_up_count > 0 && (
                                <span>{lead.follow_up_count} follow-ups</span>
                              )}
                            </div>
                            {lead.next_follow_up_date && (
                              <p className="text-xs text-amber-600 mt-1">
                                Next follow-up: {format(new Date(lead.next_follow_up_date), "MMM d, yyyy")}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          {lead.stage !== "purchased" && lead.stage !== "lost" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedLead(lead);
                                setStageUpdate({ stage: lead.stage, notes: "", next_follow_up_date: "" });
                                setStageDialogOpen(true);
                              }}
                            >
                              Update Stage
                            </Button>
                          )}
                          {(lead.stage === "purchased" || lead.stage === "lost") && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleReopenLead(lead.id)}
                            >
                              <RotateCcw className="w-3 h-3 mr-1" />
                              Reopen
                            </Button>
                          )}
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No leads found</p>
                <Button variant="link" onClick={() => setDialogOpen(true)}>Create your first lead</Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Update Stage Dialog */}
        <Dialog open={stageDialogOpen} onOpenChange={setStageDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Lead Stage</DialogTitle>
              <DialogDescription>
                {selectedLead && `Updating stage for ${getPatientInfo(selectedLead.patient_id).name}`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>New Stage</Label>
                <Select value={stageUpdate.stage} onValueChange={(v) => setStageUpdate({...stageUpdate, stage: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LEAD_STAGES.map(s => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.icon} {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes</Label>
                <Input
                  placeholder="Add notes about this update..."
                  value={stageUpdate.notes}
                  onChange={(e) => setStageUpdate({...stageUpdate, notes: e.target.value})}
                />
              </div>
              <div>
                <Label>Next Follow-up Date</Label>
                <Input
                  type="date"
                  value={stageUpdate.next_follow_up_date}
                  onChange={(e) => setStageUpdate({...stageUpdate, next_follow_up_date: e.target.value})}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setStageDialogOpen(false)}>Cancel</Button>
                <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleUpdateStage}>
                  Update Stage
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
